import React, { useState, useEffect } from 'react';
import api, { authAPI } from '../services/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../stores/authStore';
import { useNavigate } from 'react-router-dom';
import PageHero from '../components/Common/PageHero';
import MetricCard from '../components/Common/MetricCard';
import DataGrid from '../components/Common/DataGrid';

import { UserRole } from '../stores/authStore';

interface User {
  id: number;
  email: string;
  username: string;
  isActive: boolean;
  isAdmin: boolean;
  role: UserRole;
  isSuperAdmin: boolean;
  isAdminOrAbove: boolean;
  hasAdminAccess: boolean;
  hasViewOnlyAccess: boolean;
  emailVerified: boolean;
  phoneNumber?: string;
  createdAt: string;
  formsCount?: number;
  forms?: {
    length: number;
  };
}

interface UserFormData {
  email: string;
  username: string;
  password?: string;
  isAdmin: boolean;
  role: UserRole;
  phoneNumber?: string;
}

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    username: '',
    password: undefined,
    isAdmin: false,
    role: UserRole.USER,
    phoneNumber: ''
  });
  const { user: currentUser } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/auth/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Prepare update payload
        const updateData: any = {
          email: formData.email,
          username: formData.username,
          isAdmin: formData.isAdmin,
          role: formData.role,
          phoneNumber: formData.phoneNumber || undefined,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await api.put(`/api/auth/users/${editingUser.id}`, updateData);
        toast.success('User updated successfully! 👤');
      } else {
        // Use helper that hits the correct endpoint; no password is needed for admin-created users
        const createdUser = await authAPI.createUser(formData.email, formData.username, formData.isAdmin, formData.role, formData.phoneNumber);
        if (createdUser?.emailSent === false) {
          toast.error('User created, but the verification email was not sent.');
        } else {
          toast.success('User created successfully! ✨');
        }
      }

      fetchUsers();
      resetForm();
    } catch (error: any) {
      const message = error.response?.data?.message || error.response?.data?.detail || 'Operation failed';
      toast.error(message);
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      username: user.username,
      password: undefined,
      isAdmin: user.isAdmin,
      role: user.role,
      phoneNumber: user.phoneNumber || ''
    });
    setShowModal(true);
  };

  const handleToggleActive = async (userId: number, isActive: boolean) => {
    try {
      await api.put(`/api/auth/users/${userId}`, { isActive: !isActive });
      toast.success(`User ${!isActive ? 'activated' : 'deactivated'} successfully! 🔄`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user status');
    }
  };

  const handleSendVerification = async (userId: number) => {
    try {
      const response = await api.post(`/api/auth/users/${userId}/send-verification`);
      if (response.data?.emailSent === false) {
        toast.error(response.data?.message || 'Failed to send verification email');
        return;
      }
      toast.success(response.data?.message || 'Verification email sent! 📧');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to send verification email');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      username: '',
      password: undefined,
      isAdmin: false,
      role: UserRole.USER,
      phoneNumber: ''
    });
    setEditingUser(null);
    setShowModal(false);
  };

  const getStatusBadge = (user: User) => {
    if (!user.isActive) {
      return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Inactive</span>;
    }
    if (!user.emailVerified) {
      return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">Unverified</span>;
    }
    return <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Active</span>;
  };

  const filteredUsers = users.filter((entry) => {
    const haystack = `${entry.username} ${entry.email} ${entry.role} ${entry.phoneNumber || ''}`.toLowerCase();
    return haystack.includes(search.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHero
        badge={{ label: 'Identity and access', tone: 'green' }}
        title="User Management"
        description="Create users, assign roles, verify accounts, and manage access from one consistent identity workspace."
        stats={[
          { icon: 'fas fa-users', label: `${users.length} users` },
          { icon: 'fas fa-user-check', label: `${users.filter(u => u.isActive && u.emailVerified).length} active` },
        ]}
        actions={
          <>
            <button onClick={() => setShowModal(true)} className="rounded-2xl bg-white px-6 py-3 font-semibold text-slate-900 hover:bg-slate-100">
              Add user
            </button>
            <button onClick={() => navigate('/audit')} className="rounded-2xl bg-white/10 px-6 py-3 font-semibold text-white hover:bg-white/15">
              Audit trail
            </button>
            <button onClick={() => navigate('/email-management')} className="rounded-2xl bg-white/10 px-6 py-3 font-semibold text-white hover:bg-white/15">
              Email management
            </button>
            <button onClick={() => navigate('/login-audits')} className="rounded-2xl bg-white/10 px-6 py-3 font-semibold text-white hover:bg-white/15">
              Login audits
            </button>
          </>
        }
      />

      {/* Enhanced Stats Cards */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Total users" value={users.length} hint="All registered accounts" icon="fas fa-users" accent="blue" />
        <MetricCard label="Active users" value={users.filter(u => u.isActive && u.emailVerified).length} hint="Verified and active" icon="fas fa-user-check" accent="green" />
        <MetricCard label="Administrators" value={users.filter(u => u.hasAdminAccess).length} hint="Admin access holders" icon="fas fa-user-shield" accent="violet" />
        <MetricCard label="Pending verification" value={users.filter(u => !u.emailVerified).length} hint="Awaiting email verification" icon="fas fa-user-clock" accent="amber" />
      </div>

      <div className="rounded-2xl border border-gray-100 bg-white p-6 shadow-sm">
        <input
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search users by name, email, or role..."
          className="w-full rounded-2xl bg-white px-5 py-3 ring-1 ring-inset ring-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
        />
      </div>

      <DataGrid
        title="System users"
        description={`${filteredUsers.length} of ${users.length} users`}
        rows={filteredUsers}
        emptyText="No users found for the current search."
        columns={[
          {
            key: 'user',
            header: 'User',
            render: (entry) => (
              <div className="flex items-center">
                <div className={`flex h-12 w-12 items-center justify-center rounded-full ${entry.isAdmin ? 'bg-gradient-to-br from-purple-500 to-indigo-600' : 'bg-gradient-to-br from-blue-500 to-teal-600'}`}>
                  <span className="text-sm font-medium text-white">{entry.username.charAt(0).toUpperCase()}</span>
                </div>
                <div className="ml-4">
                  <div className="text-sm font-medium text-gray-900">
                    {entry.username}
                    {entry.id === Number(currentUser?.id) && <span className="ml-2 inline-flex rounded-full bg-blue-100 px-2 py-1 text-xs font-semibold text-blue-800">You</span>}
                  </div>
                  <div className="text-sm text-gray-500">{entry.email}</div>
                </div>
              </div>
            ),
          },
          { key: 'role', header: 'Role', render: (entry) => <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">{String(entry.role).replace(/_/g, ' ')}</span> },
          { key: 'phone', header: 'WhatsApp', render: (entry) => (
            <span className="text-sm text-gray-600">
              {entry.phoneNumber ? (
                <span className="inline-flex items-center"><i className="fab fa-whatsapp text-green-500 mr-1"></i>{entry.phoneNumber}</span>
              ) : (
                <span className="text-gray-400 italic">Not set</span>
              )}
            </span>
          )},
          { key: 'status', header: 'Status', render: (entry) => getStatusBadge(entry) },
          { key: 'forms', header: 'Forms', render: (entry) => `${entry.formsCount ?? entry.forms?.length ?? 0} forms` },
          { key: 'created', header: 'Created', render: (entry) => new Date(entry.createdAt).toLocaleDateString() },
          {
            key: 'actions',
            header: 'Actions',
            render: (entry) => (
              <div className="flex justify-end space-x-2">
                {!entry.emailVerified && (
                  <button onClick={() => handleSendVerification(entry.id)} className="rounded-lg p-2 text-blue-600 hover:bg-blue-50 hover:text-blue-900" title="Send Verification Email">
                    <i className="fas fa-envelope"></i>
                  </button>
                )}
                <button onClick={() => handleEdit(entry)} className="rounded-lg p-2 text-indigo-600 hover:bg-indigo-50 hover:text-indigo-900" title="Edit User">
                  <i className="fas fa-edit"></i>
                </button>
                <button
                  onClick={() => handleToggleActive(entry.id, entry.isActive)}
                  className={`rounded-lg p-2 ${entry.isActive ? 'text-orange-600 hover:bg-orange-50 hover:text-orange-900' : 'text-green-600 hover:bg-green-50 hover:text-green-900'}`}
                  title={entry.isActive ? 'Deactivate User' : 'Activate User'}
                >
                  <i className={`fas ${entry.isActive ? 'fa-user-slash' : 'fa-user-check'}`}></i>
                </button>
              </div>
            ),
          },
        ]}
      />

      {/* Modal for Create/Edit User */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8 relative">
              {/* Close button */}
              <button
                onClick={resetForm}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="fas fa-times text-xl"></i>
              </button>

              <div className="mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {editingUser ? 'Edit User' : 'Create New User'}
                </h3>
                <p className="text-gray-600">
                  {editingUser ? 'Update user information and permissions' : 'Add a new user to the system'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Username *
                    </label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => setFormData({...formData, username: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <i className="fab fa-whatsapp text-green-600 mr-1"></i>
                      WhatsApp Number
                    </label>
                    <input
                      type="tel"
                      value={formData.phoneNumber || ''}
                      onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                      placeholder="e.g. +263771234567"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Required for WhatsApp OTP login. Include country code.
                    </p>
                  </div>

                  {editingUser && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Password (leave blank to keep current)
                      </label>
                      <input
                        type="password"
                        value={formData.password || ''}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                        minLength={6}
                      />
                      <p className="text-xs text-gray-500 mt-2 flex items-center">
                        <i className="fas fa-info-circle mr-1"></i>
                        Leave blank to keep the current password
                      </p>
                    </div>
                  )}

                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isAdmin"
                        checked={formData.isAdmin}
                        onChange={(e) => setFormData({...formData, isAdmin: e.target.checked})}
                        className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      />
                      <label htmlFor="isAdmin" className="ml-2 block text-sm text-gray-900">
                        <i className="fas fa-crown text-purple-600 mr-2"></i>
                        Administrator privileges
                      </label>
                    </div>
                    <p className="text-xs text-gray-600 mt-2 ml-6">
                      Administrators can manage users, packages, and all system settings
                    </p>
                  </div>

                  <div className="bg-indigo-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <i className="fas fa-shield-alt text-indigo-600 mr-2"></i>
                      User Role
                    </label>
                    <select
                      value={formData.role}
                      onChange={(e) => setFormData({...formData, role: e.target.value as UserRole})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                    >
                      <option value={UserRole.USER}>Monitor Manager</option>
                      <option value={UserRole.ACCOUNT_MANAGER}>Account Manager</option>
                      <option value={UserRole.CUSTOMER_SUPPORT}>Customer Support</option>
                      <option value={UserRole.AUDITOR}>Auditor</option>
                      <option value={UserRole.ADMIN}>Administrator</option>
                      <option value={UserRole.SUPER_ADMIN}>Super Administrator</option>
                    </select>
                    <p className="text-xs text-gray-600 mt-2">
                      {formData.role === UserRole.SUPER_ADMIN && "Super Admin has full access to everything including user role management"}
                      {formData.role === UserRole.ADMIN && "Admin can manage users, forms, and system settings"}
                      {formData.role === UserRole.ACCOUNT_MANAGER && "Account Manager can manage customer accounts and service forms"}
                      {formData.role === UserRole.CUSTOMER_SUPPORT && "Customer Support has view-only access to forms with export functionality"}
                      {formData.role === UserRole.AUDITOR && "Auditor has view-only access to all pages for auditing purposes"}
                      {formData.role === UserRole.USER && "Monitor Manager with basic access to forms and data"}
                    </p>
                  </div>
                </div>

                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all duration-200 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 font-medium shadow-lg"
                  >
                    <i className={`fas ${editingUser ? 'fa-save' : 'fa-user-plus'} mr-2`}></i>
                    {editingUser ? 'Update' : 'Create'} User
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users; 
